import React, { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Target, Award, Users, Building2, TrendingUp, Shield, Leaf, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const { t, language } = useLanguage();

  // Scroll reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const values = [
    { icon: Award, title: t('about.values.quality'), description: t('about.values.qualityDesc') },
    { icon: TrendingUp, title: t('about.values.innovation'), description: t('about.values.innovationDesc') },
    { icon: Shield, title: t('about.values.integrity'), description: t('about.values.integrityDesc') },
    { icon: Leaf, title: t('about.values.sustainability'), description: t('about.values.sustainabilityDesc') }
  ];

  const timeline = language === 'en' ? [
    { year: '2010', title: 'Company Founded', description: 'Darwazay Garmyan established with a vision to transform residential development in Kurdistan Region' },
    { year: '2015', title: 'Major Expansion', description: 'Launched multiple residential projects and expanded team to 100+ professionals' },
    { year: '2020', title: 'Innovation Leader', description: 'Introduced modern construction techniques and sustainable building practices' },
    { year: '2025', title: 'Regional Pioneer', description: 'Recognized as leading residential developer with 10,000+ happy residents' }
  ] : language === 'ar' ? [
    { year: '2010', title: 'تأسيس الشركة', description: 'تأسست درواز كرميان برؤية لتحويل التطوير السكني في إقليم كردستان' },
    { year: '2015', title: 'توسع كبير', description: 'أطلقت مشاريع سكنية متعددة ووسعت الفريق إلى أكثر من 100 محترف' },
    { year: '2020', title: 'رائد الابتكار', description: 'قدمت تقنيات بناء حديثة وممارسات بناء مستدامة' },
    { year: '2025', title: 'رائد إقليمي', description: 'معترف بها كمطور سكني رائد مع أكثر من 10,000 ساكن سعيد' }
  ] : [
    { year: '2010', title: 'دامەزراندنی کۆمپانیا', description: 'دەروازەی گەرمیان دامەزرا بە بینینێک بۆ گۆڕینی گەشەپێدانی نیشتەجێبوون لە هەرێمی کوردستان' },
    { year: '2015', title: 'فراوانبوونی گەورە', description: 'چەندین پڕۆژەی نیشتەجێبوون دەستپێکرا و تیمەکە فراوان بوو بۆ زیاتر لە 100 پسپۆڕ' },
    { year: '2020', title: 'پێشەنگی داهێنان', description: 'تەکنیکی بیناسازی مۆدێرن و پراکتیزی بیناسازی بەردەوام ناساند' },
    { year: '2025', title: 'پێشەنگی ناوچەیی', description: 'ناسراو وەک گەشەپێدەری نیشتەجێبوونی پێشەنگ لەگەڵ زیاتر لە 10,000 دانیشتووی دڵخۆش' }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black z-10" />
        <img
          src="https://images.unsplash.com/photo-1742112125567-3e8967bad60f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjB0ZWFtfGVufDB8fHx8MTc2ODY4MDAzMnww&ixlib=rb-4.1.0&q=85"
          alt="About Us"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 text-center px-6 max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6 font-light animate-fade-in-up">
            {language === 'en' ? 'DISCOVER OUR STORY' : language === 'ar' ? 'اكتشف قصتنا' : 'چیرۆکەکەمان بدۆزەرەوە'}
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight mb-6 animate-fade-in-up delay-200">
            {t('about.hero')}
          </h1>
          <p className="text-lg text-white/70 font-light animate-fade-in-up delay-300">{t('about.subtitle')}</p>
        </div>
      </section>

      {/* Owner Section */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Owner Image */}
            <div className="scroll-reveal-left order-2 lg:order-1">
              <div className="relative aspect-[3/4] overflow-hidden bg-white/5 border border-white/10">
                {/* Placeholder for owner image - replace src with actual image */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
                      <Users className="w-16 h-16 text-white/30" />
                    </div>
                    <p className="text-white/30 text-sm font-light">
                      {language === 'en' ? 'Owner Photo' : language === 'ar' ? 'صورة المالك' : 'وێنەی خاوەن'}
                    </p>
                  </div>
                </div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 border border-white/20" />
              </div>
            </div>
            
            {/* Owner Info */}
            <div className="scroll-reveal-right order-1 lg:order-2">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6 font-light">
                {language === 'en' ? 'FOUNDER & OWNER' : language === 'ar' ? 'المؤسس والمالك' : 'دامەزرێنەر و خاوەن'}
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight leading-tight">
                {language === 'en' ? 'Ali Hadi Abdulrahman' : language === 'ar' ? 'علي هادي عبدالرحمن' : 'علي هادي عبدالرحمن'}
              </h2>
              <p className="text-xl text-white/50 font-light mb-8 italic">
                {language === 'en' ? 'Known as Ali Kelari' : language === 'ar' ? 'المعروف بـ علي كلاري' : 'ناسراوە بە علی کەلاری'}
              </p>
              <p className="text-base sm:text-lg text-white/60 font-light leading-relaxed">
                {language === 'en' 
                  ? 'Owner and implementer of the projects "Dli Shar, Dina City, Bakrajo Hills, Sulaymaniyah Corniche" which are executed by Darwazay Garmyan group of companies, owned by Mr. Ali Hadi Abdulrahman, known as Ali Kelari.'
                  : language === 'ar'
                  ? 'مالك ومنفذ مشاريع "دلي شار، دينا سيتي، باكراجو هيلز، كورنيش السليمانية" التي تنفذها مجموعة شركات درواز كرميان، والمملوكة للسيد علي هادي عبدالرحمن، المعروف بـ علي كلاري.'
                  : 'خاوەن و جێبەجێکاری پڕۆژەکانی "دڵی شار، دینا سیتی، بەکرەجۆ هێڵس، سلێمانی کۆرنیش" کە لەلایەن گروپی کۆمپانیاکانی دەروازەی گەرمیانەوە جێبەجێ دەکرێت و کە خاوەندارییەتی دەکرێت لە لایەن بەرێز علي هادي عبدالرحمن. کە ناسراوە بە علی کەلاری.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-32 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="scroll-reveal-left">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6 font-light">
                {language === 'en' ? 'OUR STORY' : language === 'ar' ? 'قصتنا' : 'چیرۆکەکەمان'}
              </p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8 tracking-tight leading-tight">
                {t('about.story.title')}
              </h2>
              <p className="text-base sm:text-lg text-white/60 font-light leading-relaxed mb-8">
                {t('about.story.content')}
              </p>
              <div className="space-y-4">
                {(language === 'en' 
                  ? ['Construction of new roads and highways', 'Development of residential communities', 'Tourism project implementation']
                  : language === 'ar'
                  ? ['إنشاء طرق وطرق سريعة جديدة', 'تطوير مجتمعات سكنية', 'تنفيذ مشاريع السياحة']
                  : ['دروستکردنی ڕێگا و شارەڕێی نوێ', 'گەشەپێدانی کۆمەڵگەی نیشتەجێبوون', 'جێبەجێکردنی پڕۆژەی گەشتیاری']
                ).map((item, index) => (
                  <div key={index} className="flex items-center gap-4 group">
                    <div className="w-8 h-px bg-white/30 group-hover:w-12 group-hover:bg-white transition-all duration-300" />
                    <span className="text-white/70 font-light">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="scroll-reveal-right">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src="https://www.darwazaygarmyan.com/_next/image?url=%2Fgallery%2Fdina_city%2Finterior%2F4.png&w=3840&q=75"
                  alt="Our Story"
                  className="w-full h-full object-cover"
                />
                <div className="absolute -bottom-6 -right-6 w-32 h-32 border border-white/20" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-32 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="scroll-reveal-scale border border-white/10 p-12 lg:p-16 hover:border-white/30 transition-colors duration-500 group">
              <Target className="w-12 h-12 text-white/50 mb-8 group-hover:text-white transition-colors" />
              <h3 className="text-2xl sm:text-3xl font-light text-white mb-6 tracking-wide">
                {t('about.vision.title')}
              </h3>
              <p className="text-white/60 font-light leading-relaxed">{t('about.vision.content')}</p>
            </div>
            <div className="scroll-reveal-scale delay-200 border border-white/10 p-12 lg:p-16 hover:border-white/30 transition-colors duration-500 group">
              <Award className="w-12 h-12 text-white/50 mb-8 group-hover:text-white transition-colors" />
              <h3 className="text-2xl sm:text-3xl font-light text-white mb-6 tracking-wide">
                {t('about.mission.title')}
              </h3>
              <p className="text-white/60 font-light leading-relaxed">{t('about.mission.content')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="mb-20 scroll-reveal">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 font-light">
              {language === 'en' ? 'WHAT WE STAND FOR' : language === 'ar' ? 'ما نمثله' : 'ئەوەی پابەندینی'}
            </p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
              {t('about.values.title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className={`scroll-reveal-scale border border-white/10 p-10 hover:border-white/30 transition-all duration-500 group tilt-3d`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <value.icon className="w-10 h-10 text-white/40 mb-8 group-hover:text-white transition-colors" />
                <h3 className="text-lg font-light text-white mb-4 tracking-wide">{value.title}</h3>
                <p className="text-white/50 font-light leading-relaxed text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-32 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <div className="mb-20 scroll-reveal">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 font-light">
              {language === 'en' ? 'OUR MILESTONES' : language === 'ar' ? 'معالمنا' : 'بەشداریەکانمان'}
            </p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
              {language === 'en' ? 'Our Journey' : language === 'ar' ? 'رحلتنا' : 'گەشتەکەمان'}
            </h2>
          </div>
          <div className="space-y-0">
            {timeline.map((item, index) => (
              <div key={index} className={`scroll-reveal relative flex gap-8 lg:gap-16 pb-16 ${index === timeline.length - 1 ? '' : ''}`}>
                {/* Year */}
                <div className="flex-shrink-0 w-24 lg:w-32 text-right">
                  <span className="text-4xl lg:text-5xl font-light text-white/20">{item.year}</span>
                </div>
                {/* Timeline Line */}
                <div className="relative flex-shrink-0">
                  <div className={`absolute top-3 left-1 w-px bg-white/20 ${index === timeline.length - 1 ? 'h-0' : 'h-full'}`} />
                  <div className="w-3 h-3 bg-white relative z-10" />
                </div>
                {/* Content */}
                <div className="flex-1 pt-0">
                  <h3 className="text-xl lg:text-2xl font-light text-white mb-4 tracking-wide">{item.title}</h3>
                  <p className="text-white/50 font-light leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            {[
              { icon: Users, value: '10,000+', label: 'Happy Residents' },
              { icon: Building2, value: '11+', label: 'Major Projects' },
              { icon: Award, value: '10+', label: 'Years of Excellence' }
            ].map((stat, index) => (
              <div key={index} className="text-center scroll-reveal-scale" style={{ animationDelay: `${index * 0.1}s` }}>
                <stat.icon className="w-10 h-10 mx-auto mb-6 opacity-30" />
                <div className="text-5xl lg:text-6xl font-light mb-4">{stat.value}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-black/50 font-light">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-8 tracking-tight scroll-reveal">
            {language === 'en' ? 'Ready to Build Your Future?' : language === 'ar' ? 'هل أنت مستعد لبناء مستقبلك؟' : 'ئامادەیت بۆ بنیاتنانی داهاتووت؟'}
          </h2>
          <Link
            to="/contact"
            className="btn-luxury inline-flex items-center gap-3 px-10 py-4 bg-white text-black text-xs uppercase tracking-[0.2em] font-medium hover:bg-white/90 scroll-reveal delay-200"
          >
            {t('cta.button')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
